import React, { useEffect, useMemo, useState } from 'react';
import _get from 'lodash/get';
import queryString from 'query-string';
import { useLocation, useParams } from 'react-router';
import { BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION } from 'pages/Attributes/consts';

import Page from 'components/Page';
import PageTitle from 'components/Page/PageTitle';
import AttributeTypeCard, { attributeTypes } from './AttributeTypeCard';
import Typography from '@material-ui/core/Typography';
import PageTypesDialog from 'pages/Pages/components/PageTypesDialog';

import makeStyles from '@material-ui/core/styles/makeStyles';
import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

const TEXT_CARDS = ['text', 'textLong'];
const CONTACT_CARDS = ['email', 'phone', 'messageAddress'];
const CHIP_CARDS = [
  'textSelect',
  'multiSelect',
  'autocompleteTextCollection',
  'textCollection',
  'boolean',
];
const SUB_ATTRIBUTE_CHIP_CARDS = ['multiSelect', 'autocompleteTextCollection', 'textCollection'];
const OTHER_CARDS = ['hyperlink', 'linkCollection', 'date', 'number'];
const SUB_ATTRIBUTE_TEXT_CARDS = ['text', 'textSelect'];
const PAGE_CARDS = [BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION];

function AttributesChooseNew() {
  const classes = useStyles();
  const location = useLocation();
  const search = location.search;
  const query = useMemo(() => queryString.parse(search), [location.search]);
  const parentObjectKey = _get(query, 'parentObjectKey');
  const { entityTypeKey } = useParams<{ entityTypeKey: string }>();
  const subAttribute = Boolean(parentObjectKey);
  const isUser = entityTypeKey === PERSON_ENTITY_TYPE_KEY;
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [newAttributeType, setNewAttributeType] = useState<
    typeof BASE_FIELD_PAGE | typeof BASE_FIELD_PAGE_COLLECTION
  >(BASE_FIELD_PAGE);

  useEffect(() => {
    document.title = subAttribute ? 'New Sub-Attribute' : 'New Attribute';
  }, []);

  const handleClick = (pageType: typeof BASE_FIELD_PAGE | typeof BASE_FIELD_PAGE_COLLECTION) => {
    setNewAttributeType(pageType);
    setPageDialogOpen(true);
  };

  return (
    <div className={classes.root}>
      <Page classes={{ root: classes.pageRoot }}>
        <div className={classes.title}>
          <PageTitle title="Select Attribute Type" />
        </div>
        <div>
          <Typography variant="h5">Text</Typography>
          {subAttribute ? (
            <div className={classes.cardSection}>
              {SUB_ATTRIBUTE_TEXT_CARDS.map((name, index) => {
                let item = _get(attributeTypes, name);
                return (
                  <AttributeTypeCard
                    item={item}
                    key={`attribute-type-${index}`}
                    link
                    parentObjectKey={parentObjectKey}
                  />
                );
              })}
            </div>
          ) : (
            <div className={classes.cardSection}>
              {TEXT_CARDS.map((name, index) => {
                let item = _get(attributeTypes, name);
                return (
                  <AttributeTypeCard
                    item={item}
                    key={`attribute-type-${index}`}
                    link
                    parentObjectKey={parentObjectKey}
                  />
                );
              })}
            </div>
          )}
          {!subAttribute && (
            <>
              <Typography variant="h5">Contact</Typography>
              <div className={classes.cardSection}>
                {CONTACT_CARDS.map((name, index) => {
                  let item = _get(attributeTypes, name);
                  return (
                    <AttributeTypeCard
                      item={item}
                      key={`attribute-type-${index}`}
                      link
                      parentObjectKey={parentObjectKey}
                    />
                  );
                })}
              </div>
            </>
          )}
          <Typography variant="h5">Select and Text (Chip) Collection</Typography>
          {subAttribute ? (
            <div className={classes.cardSection}>
              {SUB_ATTRIBUTE_CHIP_CARDS.map((name, index) => {
                let item = _get(attributeTypes, name);
                return (
                  <AttributeTypeCard
                    item={item}
                    key={`attribute-type-${index}`}
                    link
                    parentObjectKey={parentObjectKey}
                  />
                );
              })}
            </div>
          ) : (
            <div className={classes.cardSection}>
              {CHIP_CARDS.map((name, index) => {
                let item = _get(attributeTypes, name);
                return (
                  <AttributeTypeCard
                    item={item}
                    key={`attribute-type-${index}`}
                    link
                    parentObjectKey={parentObjectKey}
                  />
                );
              })}
            </div>
          )}
          {isUser && (
            <>
              <Typography variant="h5">Pages</Typography>
              <Typography variant="subtitle2">
                By creating a Page or Page Collection, you will create a new Page type which you can
                manage in the "Pages" tab.
              </Typography>
              <div className={classes.cardSection}>
                {PAGE_CARDS.map((name, index) => {
                  let item = _get(attributeTypes, name);
                  return (
                    <AttributeTypeCard
                      item={item}
                      key={`attribute-type-${index}`}
                      link={false}
                      parentObjectKey={parentObjectKey}
                      onClick={() =>
                        handleClick(
                          item.link as typeof BASE_FIELD_PAGE | typeof BASE_FIELD_PAGE_COLLECTION,
                        )
                      }
                    />
                  );
                })}
              </div>
            </>
          )}
          {!subAttribute && (
            <>
              <Typography variant="h5">Others</Typography>
              <div className={classes.cardSection}>
                {OTHER_CARDS.map((name, index) => {
                  let item = _get(attributeTypes, name);
                  return (
                    <AttributeTypeCard
                      item={item}
                      key={`attribute-type-${index}`}
                      link
                      parentObjectKey={parentObjectKey}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Page>
      <PageTypesDialog
        newAttributeType={newAttributeType}
        open={pageDialogOpen}
        onSetOpen={setPageDialogOpen}
      />
    </div>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: '100%',
  },
  pageRoot: {
    height: '100%',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
  },
  cardSection: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
  },
  cardRoot: {
    maxWidth: 214,
    marginRight: theme.spacing(2),
    '&:hover': {
      boxShadow:
        '0px 6px 14px -6px rgba(33, 33, 33, 0.12), 0px 10px 32px -4px rgba(33, 33, 33, 0.1)',
    },
  },
  content: {
    padding: 0,
  },
  cardTitle: {
    padding: theme.spacing(),
  },
  cardContent: {
    padding: theme.spacing(1, 1, 1.5, 1),
  },
  icon: {
    width: 214,
    height: 102,
  },
}));

export default AttributesChooseNew;
